import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader, ResponsiveFooter } from '../ui/ResponsiveLayout';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import type { UnifiedContact } from '../../services/unifiedContactService';
import { unifiedContactService } from '../../services/unifiedContactService';
import ManualContactModal from '../ui/ManualContactModal';
import { API_BASE_URL } from '../../config/api';
import { ENABLE_BENEFICIARY_PORTAL } from '../../config/planAccessConfig';

interface SelectedExecutor {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const Frame302: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();

  const [contacts, setContacts] = useState<UnifiedContact[]>([]);
  const [selectedExecutors, setSelectedExecutors] = useState<SelectedExecutor[]>([]);
  const [executorNote, setExecutorNote] = useState<string>('');
  const [showManualModal, setShowManualModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasHydratedFromSession = useRef(false); // useRef to prevent overwriting when session updates

  // Load contacts and saved executors (only hydrate from session on initial load)
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Load contacts from unified service
        const allContacts = await unifiedContactService.getAllContacts();
        setContacts(allContacts);

        // Hydrate from session only on first load - prevents overwriting when session updates
        // (e.g. from updateActivity on click) which was breaking selection
        const savedExecutors = session.taskProgress?.executors?.answers?.selectedExecutors || [];
        const savedNote = session.taskProgress?.executors?.answers?.executorNote || '';

        if (!hasHydratedFromSession.current && savedExecutors.length > 0) {
          hasHydratedFromSession.current = true;
          setSelectedExecutors(savedExecutors);
          setExecutorNote(savedNote);
        }
      } catch (err) {
        console.error('[Frame302] Error loading data:', err);
        setError('Failed to load contacts');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [session]);

  // Toggle executor selection
  const toggleExecutor = (contact: UnifiedContact) => {
    const isSelected = selectedExecutors.some(e => e.id === contact.id);

    if (isSelected) {
      setSelectedExecutors(prev => prev.filter(e => e.id !== contact.id));
    } else {
      if (!contact.email) {
        alert('This contact needs an email address to be invited as an executor.');
        return;
      }
      setSelectedExecutors(prev => [...prev, {
        id: contact.id,
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email
      }]);
    }

    console.log('[Frame302] Selected executors:', selectedExecutors.length);
  };

  // Handle manual contact submission
  const handleManualContactSubmit = async (contactData: unknown) => {
    try {
      const newContact = await unifiedContactService.createContact({
        firstName: contactData.firstName.trim(),
        lastName: contactData.lastName.trim(),
        email: contactData.email?.trim() || '',
        phone: contactData.phone?.trim(),
        relationship: 'executor',
        source: 'manual',
        notes: 'Added as executor'
      });

      // Reload contacts
      const allContacts = await unifiedContactService.getAllContacts();
      setContacts(allContacts);

      // Auto-select the new contact as executor
      if (newContact && contactData.email) {
        setSelectedExecutors(prev => [...prev, {
          id: newContact.id,
          firstName: contactData.firstName.trim(),
          lastName: contactData.lastName.trim(),
          email: contactData.email.trim()
        }]);
      }

      setShowManualModal(false);
    } catch (err: unknown) {
      if (err.code === 'DUPLICATE_CONTACT') {
        alert(`A contact with email ${contactData.email} already exists.`);
      } else {
        alert('Failed to add contact. Please try again.');
      }
      throw err;
    }
  };

  const nextRoute = ENABLE_BENEFICIARY_PORTAL ? '/frame-303' : '/frame-304';

  // Send executor invites and navigate
  const handleNext = async () => {
    if (selectedExecutors.length === 0) {
      await updateTaskProgress('executors', { selectedExecutors: [], executorNote: '' });
      // Allow skipping executor selection
      navigate(nextRoute);
      return;
    }
    
    setIsSending(true);
    setError(null);


    try {
      // Save executors to session
      await updateTaskProgress('executors', { selectedExecutors, executorNote: executorNote });
      
      const token = localStorage.getItem('account-token');

      const response = await fetch(`${API_BASE_URL}/api/executors/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          executors: selectedExecutors.map(e => ({
            email: e.email,
            name: `${e.firstName} ${e.lastName}`
          })),
          ...(executorNote.trim() && { personalMessage: executorNote.trim() })
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send executor invites');
      }
      
      console.log('[Frame302] Executor invites sent successfully');

      // Mark task progress
      
      navigate(nextRoute);
    } catch (err: unknown) {
      console.error('[Frame302] Error saving executors:', err);
      setError('Failed to save executor selection. Please try again.');
      setIsSending(false);
    }
  };

  const handleBack = () => {
    // Save current selection before going back (including empty state)
    updateTaskProgress('executors', {
      selectedExecutors,
      executorNote: executorNote
    });
    navigate('/frame-301');
  };

  const getPersonName = (contact: UnifiedContact) => {
    return `${contact.firstName} ${contact.lastName}`.trim();
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader
          title="Confirm executor access"
        />

        <div className="max-w-4xl space-y-6">
          {/* Description */}
          <div className="space-y-4">
            <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
            Your executor is responsible for carrying out this plan.
            </p>
            <p className="text-stone-600 text-sm">
            An executor is responsible for managing your estate and carrying out your wishes after you pass. 
            Choose someone you trust who is organized and capable of handling financial and legal matters. 
            You can change or update executor access at any time.
            </p>
          </div>

          {/* Legal Disclaimer */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 text-xl">⚖️</div>
              <div>
                <h3 className="font-semibold text-yellow-900 mb-1">Important Note</h3>
                <p className="text-sm text-yellow-800">
                 Inviting an executor gives them access to your plan through End of an Era. 
                 This does not create a legally binding appointment, and you can change or revoke access at any time.
                </p>
              </div>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          )}

          {/* Contact Selection */}
          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-4 border-cyan-700 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-stone-600 mt-4">Loading contacts...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Selected executors summary */}
              {selectedExecutors.length > 0 && (
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                  <h3 className="font-semibold text-cyan-900 mb-2">
                    Selected Executor{selectedExecutors.length > 1 ? 's' : ''} ({selectedExecutors.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedExecutors.map(executor => (
                      <span
                        key={executor.id}
                        className="inline-flex items-center gap-2 px-3 py-1 bg-white rounded-full text-sm text-cyan-800 border border-cyan-300"
                      >
                        {executor.firstName} {executor.lastName}
                        <button
                          onClick={() => setSelectedExecutors(prev => prev.filter(e => e.id !== executor.id))}
                          className="text-cyan-600 hover:text-cyan-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact list */}
              <div className="bg-white rounded-lg border border-stone-200">
                <div className="p-4 border-b border-stone-200 flex justify-between items-center">
                  <h3 className="font-semibold text-stone-800">Your Contacts</h3>
                  <button
                    onClick={() => setShowManualModal(true)}
                    className="text-sm text-cyan-700 hover:text-cyan-800 font-medium"
                  >
                    + Add New Contact
                  </button>
                </div>

                {contacts.length === 0 ? (
                  <div className="p-8 text-center text-stone-500">
                    <p>No contacts found.</p>
                    <button
                      onClick={() => setShowManualModal(true)}
                      className="mt-2 text-cyan-700 hover:text-cyan-800 font-medium"
                    >
                      Add your first contact
                    </button>
                  </div>
                ) : (
                  <div className="max-h-96 overflow-y-auto">
                    {contacts.map(contact => {
                      const isSelected = selectedExecutors.some(e => e.id === contact.id);
                      
                      return (
                        <div
                          key={contact.id}
                          onClick={() => toggleExecutor(contact)}
                          className={`p-4 border-b border-stone-100 last:border-b-0 cursor-pointer transition-colors ${
                            isSelected
                              ? 'bg-cyan-50'
                              : 'hover:bg-stone-50'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            {/* Checkbox */}
                            <div
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                isSelected
                                  ? 'bg-cyan-700 border-cyan-700'
                                  : 'border-stone-300'
                              }`}
                            >
                              {isSelected && (
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>

                            {/* Contact info */}
                            <div className="flex-1">
                              <div className="font-medium text-stone-800">
                                {getPersonName(contact)}
                              </div>
                              {contact.email ? (
                                <div className="text-sm text-stone-600">{contact.email}</div>
                              ) : (
                                <div className="text-sm text-orange-600">No email address</div>
                              )}
                              {contact.relationship && (
                                <div className="text-xs text-stone-500 capitalize">{contact.relationship}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    
                  </div>
                )}
              </div>
              {/* Optional message for selected executors */}
              {selectedExecutors.length > 0 && (
                <div className="bg-white border border-stone-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Personal Message (Optional)
                  </label>
                  <textarea
                    value={executorNote}
                    onChange={(e) => setExecutorNote(e.target.value)}
                    placeholder="Add a personal note that will be included in the invitation email to your executor(s)..."
                    rows={4}
                    className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm text-stone-700 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-stone-500">
                    This message will be included in the email invitation sent to all selected executors.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Confirm Plan Access"
        subtitle="Confirm Executor Access"
        progressPercent={ENABLE_BENEFICIARY_PORTAL ? 33 : 50}
        nextDisabled={isSending}
        onNext={handleNext}
        onBack={handleBack}
        nextText={selectedExecutors.length > 0 ? "Invite" : "Skip"}
        backText="Back"
      />

      {/* Manual Contact Modal */}
      <ManualContactModal
        open={showManualModal}
        onClose={() => setShowManualModal(false)}
        onSubmit={handleManualContactSubmit}
        loading={false}
      />
    </ResponsiveContainer>
  );
};

export default Frame302;